import {
  formatOtherLayerFieldName,
  OTHER_LAYER_FIELD_NAME_SEPARATOR,
} from 'govwell-shared/otherLayerFieldDisplay'
import { BackwardsCompatibleLocationInformationFragment, EntityFieldFragment } from 'types/graphql'

import { EntityUrlFragment } from 'src/components/TableViews/EntityDisplay'
import { EntityLayerTypes, sortEntityVersionFields } from 'src/utils/entityDisplayHelpers'

export const otherLayerEntityLinkRowKey = (entityId: number): string =>
  `other-entity-link-${entityId}`

export type IntersectingOtherEntityForPropertyTab = {
  id: number
  uuid: string
  name: string
  layer: {
    id: number
    uuid: string
    name: string
  }
  latestVersion?: {
    fields?: EntityFieldFragment[] | null
  } | null
}

export type PropertyTabOtherLayerEntityLinkRow = {
  rowKind: 'entityLink'
  key: string
  fieldName: string
  entity: EntityUrlFragment
}

export type PropertyTabOtherLayerFieldRow = {
  rowKind: 'field'
  key: string
  fieldName: string
  fieldValue: string
}

export type PropertyTabOtherLayerRow =
  | PropertyTabOtherLayerEntityLinkRow
  | PropertyTabOtherLayerFieldRow

const toEntityUrlFragment = (
  entity: IntersectingOtherEntityForPropertyTab
): EntityUrlFragment | null => {
  if (!entity.uuid || !entity.layer.uuid) {
    return null
  }

  return {
    uuid: entity.uuid,
    name: entity.name,
    layer: {
      type: EntityLayerTypes.Other,
      uuid: entity.layer.uuid,
      name: entity.layer.name,
    },
  }
}

/**
 * Converts intersecting Other-type entities into Property Tab rows: an entity
 * link row (layer name → entity detail page) followed by that entity's field rows.
 */
export const buildOtherLayerRowsForIntersectingEntities = (
  intersectingOtherEntities: IntersectingOtherEntityForPropertyTab[]
): PropertyTabOtherLayerRow[] => {
  if (!intersectingOtherEntities.length) {
    return []
  }

  // Keep rows from the same configured GIS layer together in the table
  const entitiesByLayerId = new Map<
    number,
    { layerName: string; entities: IntersectingOtherEntityForPropertyTab[] }
  >()
  for (const entity of intersectingOtherEntities) {
    const layerId = entity.layer.id
    const existingLayerGroup = entitiesByLayerId.get(layerId)
    if (existingLayerGroup) {
      existingLayerGroup.entities.push(entity)
      continue
    }

    entitiesByLayerId.set(layerId, {
      layerName: entity.layer.name,
      entities: [entity],
    })
  }

  const sortedLayerGroups = Array.from(entitiesByLayerId.values()).sort(
    (layerGroupA, layerGroupB) => layerGroupA.layerName.localeCompare(layerGroupB.layerName)
  )

  const rows: PropertyTabOtherLayerRow[] = []
  const usedFieldKeys = new Set<string>()

  for (const { layerName, entities } of sortedLayerGroups) {
    const sortedEntities = [...entities].sort((entityA, entityB) =>
      entityA.name.localeCompare(entityB.name)
    )

    for (const entity of sortedEntities) {
      // Other-layer rows always use name-priority field ordering, even when the org
      // has only one Other layer — matches entity detail views for overlay/flood data.
      const sortedFields = sortEntityVersionFields({
        fields: entity.latestVersion?.fields ?? [],
        entityLayerType: EntityLayerTypes.Other,
        organizationHasMultipleEntityLayers: true,
        entityName: entity.name,
      })

      const fieldRows: PropertyTabOtherLayerFieldRow[] = []

      for (const field of sortedFields) {
        const fieldLabel = field.entityFieldTemplate?.label?.trim()
        const fieldValue = field.stringifiedValue?.trim()
        if (!fieldLabel || !fieldValue) {
          continue
        }

        let fieldName = formatOtherLayerFieldName(layerName, fieldLabel)
        let key = fieldName

        // Multiple intersecting entities in one layer can expose the same field label
        if (usedFieldKeys.has(key)) {
          fieldName = `${layerName}${OTHER_LAYER_FIELD_NAME_SEPARATOR}${entity.name}${OTHER_LAYER_FIELD_NAME_SEPARATOR}${fieldLabel}`
          key = fieldName
        }

        usedFieldKeys.add(key)
        fieldRows.push({
          rowKind: 'field',
          fieldName,
          key,
          fieldValue,
        })
      }

      if (!fieldRows.length) {
        continue
      }

      const entityUrlFragment = toEntityUrlFragment(entity)
      if (entityUrlFragment) {
        rows.push({
          rowKind: 'entityLink',
          key: otherLayerEntityLinkRowKey(entity.id),
          fieldName: layerName,
          entity: entityUrlFragment,
        })
      }

      rows.push(...fieldRows)
    }
  }

  return rows
}

/** Field-only rows for callers that only need prefixed attribute keys/values. */
export const buildOtherLayerFieldRowsForIntersectingEntities = (
  intersectingOtherEntities: IntersectingOtherEntityForPropertyTab[]
): Array<{ fieldName: string; key: string; fieldValue: string }> =>
  buildOtherLayerRowsForIntersectingEntities(intersectingOtherEntities)
    .filter((row): row is PropertyTabOtherLayerFieldRow => row.rowKind === 'field')
    .map(({ fieldName, key, fieldValue }) => ({ fieldName, key, fieldValue }))

/** Whether an intersecting Other entity has at least one visible prefixed field row. */
export const intersectingOtherEntityHasVisibleFields = (
  entity: IntersectingOtherEntityForPropertyTab
): boolean => buildOtherLayerFieldRowsForIntersectingEntities([entity]).length > 0

/** Excludes the record's own linked entity from intersecting Other results (API includes self when types match). */
export const excludeLinkedEntityFromIntersectingOtherEntities = (
  intersectingOtherEntities: IntersectingOtherEntityForPropertyTab[],
  linkedEntityId?: number | null
): IntersectingOtherEntityForPropertyTab[] => {
  if (!linkedEntityId) {
    return intersectingOtherEntities
  }

  return intersectingOtherEntities.filter((entity) => entity.id !== linkedEntityId)
}

/** Key/value lookup for one linked location's Other-layer rows (multi-address table). */
export const buildOtherLayerFieldValuesByKeyForLocation = (
  entityVersionId: number | null | undefined,
  intersectingOtherEntitiesByEntityVersionId: Map<number, IntersectingOtherEntityForPropertyTab[]>,
  linkedEntityId?: number | null
): Map<string, string> => {
  if (!entityVersionId) {
    return new Map()
  }

  const intersectingOtherEntities = excludeLinkedEntityFromIntersectingOtherEntities(
    intersectingOtherEntitiesByEntityVersionId.get(entityVersionId) ?? [],
    linkedEntityId
  )
  const rows = buildOtherLayerFieldRowsForIntersectingEntities(intersectingOtherEntities)

  return new Map(rows.map((row) => [row.key, row.fieldValue]))
}

/** Union of all Other-layer field keys across every linked record location. */
export const collectAllOtherLayerFieldKeysForLocations = (
  locationInfos: Pick<
    BackwardsCompatibleLocationInformationFragment,
    'entityVersionId' | 'entityId'
  >[],
  intersectingOtherEntitiesByEntityVersionId: Map<number, IntersectingOtherEntityForPropertyTab[]>
): string[] => {
  const fieldKeys = new Set<string>()

  for (const locationInfo of locationInfos) {
    const fieldValuesByKey = buildOtherLayerFieldValuesByKeyForLocation(
      locationInfo.entityVersionId,
      intersectingOtherEntitiesByEntityVersionId,
      locationInfo.entityId
    )

    for (const fieldKey of fieldValuesByKey.keys()) {
      fieldKeys.add(fieldKey)
    }
  }

  return Array.from(fieldKeys).sort()
}

/** Deduped intersecting Other entities across all linked locations (for multi-address link columns). */
export const collectIntersectingOtherEntitiesForLocations = (
  locationInfos: Pick<
    BackwardsCompatibleLocationInformationFragment,
    'entityVersionId' | 'entityId'
  >[],
  intersectingOtherEntitiesByEntityVersionId: Map<number, IntersectingOtherEntityForPropertyTab[]>
): IntersectingOtherEntityForPropertyTab[] => {
  const intersectingOtherEntitiesById = new Map<number, IntersectingOtherEntityForPropertyTab>()

  for (const locationInfo of locationInfos) {
    if (!locationInfo.entityVersionId) {
      continue
    }

    for (const entity of excludeLinkedEntityFromIntersectingOtherEntities(
      intersectingOtherEntitiesByEntityVersionId.get(locationInfo.entityVersionId) ?? [],
      locationInfo.entityId
    )) {
      intersectingOtherEntitiesById.set(entity.id, entity)
    }
  }

  return Array.from(intersectingOtherEntitiesById.values()).sort((entityA, entityB) => {
    const layerCompare = entityA.layer.name.localeCompare(entityB.layer.name)
    if (layerCompare !== 0) {
      return layerCompare
    }
    return entityA.name.localeCompare(entityB.name)
  })
}

/** Whether a location intersects the given Other entity and has a linkable detail page. */
export const getIntersectingOtherEntityForLocation = (
  locationInfo: Pick<
    BackwardsCompatibleLocationInformationFragment,
    'entityVersionId' | 'entityId'
  >,
  entityId: number,
  intersectingOtherEntitiesByEntityVersionId: Map<number, IntersectingOtherEntityForPropertyTab[]>
): IntersectingOtherEntityForPropertyTab | null => {
  if (!locationInfo.entityVersionId) {
    return null
  }

  return (
    excludeLinkedEntityFromIntersectingOtherEntities(
      intersectingOtherEntitiesByEntityVersionId.get(locationInfo.entityVersionId) ?? [],
      locationInfo.entityId
    ).find((entity) => entity.id === entityId) ?? null
  )
}
